import React, { useState, useEffect, useCallback } from 'react';
import * as api from '../../services/api';
import {
  DATA_CLASSIFICATIONS,
  AUTONOMY_CHOICES,
  DPG_SCAN_LEVELS,
  dpgStoredToAction,
} from '../../data/protectionLimits';
import {
  Shield, ShieldCheck, Zap, ChevronDown, ChevronUp, Plus, Trash2, CheckCircle2,
  AlertTriangle, Activity, Lock, Database, Settings, Eye, RefreshCw,
  ArrowRight, ArrowLeft, XCircle, Check, Layers, BookOpen, Link2, BarChart3
} from 'lucide-react';

/**
 * GelPolicyWizard — Structured governance policy configuration for external agents.
 *
 * This is the GEL equivalent of PolicySkillWizard. It produces structured governance
 * policies that ExternalAgentPolicyBridge materializes into standard PolicySkillConfiguration
 * + PolicyRule + EnforcementCandidate entities — giving external agents the exact same
 * enforcement pipeline as native agents.
 *
 * Steps:
 *   1. Classification & Autonomy — data classification, autonomy cap, DPG scan level
 *   2. Enforcement Rules — rate limits, custom rules (regex, range, enum, approval gates)
 *   3. Review & Validate — summary of all rules, validation against bridge readiness
 *   4. Activate — dry-run test, activate policy configurations
 */

const BRAND = { primary: '#0d9488', dark: '#0a766b', light: '#e3f4f1', glow: 'rgba(226,74,58,0.08)' };
const STONE = {
  50: '#fafaf9', 100: '#f5f5f4', 200: '#e7e5e4', 300: '#d6d3d1',
  400: '#a8a29e', 500: '#78716c', 600: '#57534e', 700: '#44403c', 800: '#292524', 900: '#1c1917',
};
const ACCENT = {
  indigo: { bg: '#eef2ff', text: '#3730a3', border: '#c7d2fe' },
  emerald: { bg: '#ecfdf5', text: '#065f46', border: '#a7f3d0' },
  amber: { bg: '#fffbeb', text: '#92400e', border: '#fde68a' },
  red: { bg: '#fef2f2', text: '#991b1b', border: '#fecaca' },
  sky: { bg: '#f0f9ff', text: '#075985', border: '#bae6fd' },
};

// The five limits, their ladders and their wording live in one module
// (src/data/protectionLimits.js) because Protect asks the same five questions of
// the same person. Two surfaces that word the same cap differently are two
// products — and the ORDER of each ladder is the rank the backend expands a cap
// along, so a list that drifts is a choice that does not match the rule the
// gateway enforces. protectionLimits.test.js reads both ladders off
// ExternalAgentPolicyBridge and fails if either moves.
//
// The palette and the icons stay here: they are this page's skin, not the
// vocabulary, and Protect renders the same choices in its own tokens.
const CLASSIFICATION_TONE = {
  PUBLIC: ACCENT.emerald,
  INTERNAL: ACCENT.sky,
  CONFIDENTIAL: ACCENT.amber,
  RESTRICTED: ACCENT.red,
};

const AUTONOMY_ICON = {
  SUPERVISED: Eye,
  SEMI_AUTONOMOUS: Shield,
  FULLY_AUTONOMOUS: Zap,
};

const RULE_TYPES = [
  { value: 'RATE_LIMIT', label: 'Rate Limit', desc: 'Max requests per time window' },
  { value: 'REGEX', label: 'Pattern Match', desc: 'Block/warn on regex pattern match in input/output' },
  { value: 'RANGE', label: 'Numeric Range', desc: 'Enforce min/max bounds on a field' },
  { value: 'ENUM_MATCH', label: 'Allowed Values', desc: 'Restrict field to specific allowed values' },
];

const SEVERITIES = ['BLOCK', 'WARN', 'AUDIT'];

const STEPS = [
  { key: 'classify', label: 'Classification & Autonomy', icon: Database },
  { key: 'rules', label: 'Enforcement Rules', icon: Shield },
  { key: 'frameworks', label: 'Compliance Frameworks', icon: BookOpen },
  { key: 'review', label: 'Review & Validate', icon: CheckCircle2 },
  { key: 'activate', label: 'Activate', icon: Zap },
];

const BINDING_STATUS_COLORS = {
  PENDING: { bg: STONE[100], text: STONE[600], border: STONE[300] },
  SKILL_CREATED: { bg: ACCENT.sky.bg, text: ACCENT.sky.text, border: ACCENT.sky.border },
  RULES_GENERATED: { bg: ACCENT.indigo.bg, text: ACCENT.indigo.text, border: ACCENT.indigo.border },
  TESTING: { bg: ACCENT.amber.bg, text: ACCENT.amber.text, border: ACCENT.amber.border },
  ACTIVE: { bg: ACCENT.emerald.bg, text: ACCENT.emerald.text, border: ACCENT.emerald.border },
  FAILED: { bg: ACCENT.red.bg, text: ACCENT.red.text, border: ACCENT.red.border },
  ARCHIVED: { bg: STONE[100], text: STONE[500], border: STONE[300] },
};

const cardStyle = {
  background: '#fff', borderRadius: 10, border: `1px solid ${STONE[200]}`,
  padding: 16, marginBottom: 12,
};
const inputStyle = {
  width: '100%', padding: '8px 12px', borderRadius: 8,
  border: `1px solid ${STONE[200]}`, fontSize: 13, outline: 'none',
  boxSizing: 'border-box', fontFamily: 'Inter, sans-serif',
};
const selectStyle = {
  ...inputStyle, appearance: 'none',
  backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'12\' viewBox=\'0 0 12 12\'%3E%3Cpath d=\'M6 8L1 3h10z\' fill=\'%2378716c\'/%3E%3C/svg%3E")',
  backgroundRepeat: 'no-repeat', backgroundPosition: 'right 10px center', paddingRight: 28,
};
const btnPrimary = {
  padding: '10px 20px', borderRadius: 8, border: 'none', cursor: 'pointer',
  background: BRAND.primary, color: '#fff', fontSize: 13, fontWeight: 600,
  display: 'flex', alignItems: 'center', gap: 6,
};
const btnSecondary = {
  ...btnPrimary, background: STONE[100], color: STONE[700], border: `1px solid ${STONE[200]}`,
};

export default function GelPolicyWizard({ registration, onComplete, onCancel, policySkill = null }) {
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [validationResult, setValidationResult] = useState(null);

  // W1 (D-1) — governance source. Legacy: the registration's governancePolicies.
  // Policy-skill mode (policySkill provided): the skill's policyConfig IS the
  // governance set. Same shape (dataClassification, maxAutonomyLevel, …), so the
  // entire wizard UI/hydration is reused unchanged; only persistence differs.
  const gov = policySkill ? (policySkill.policyConfig || {}) : (registration?.governancePolicies || {});

  // Step 1: Classification & Autonomy
  const [dataClassification, setDataClassification] = useState(
    gov.dataClassification || 'INTERNAL'
  );
  const [maxAutonomyLevel, setMaxAutonomyLevel] = useState(
    gov.maxAutonomyLevel || 'SUPERVISED'
  );
  const [dpgScanLevel, setDpgScanLevel] = useState(
    dpgStoredToAction(gov.dpgScanLevel)
  );
  const [requiredApprovals, setRequiredApprovals] = useState(
    gov.requiredApprovals || 1
  );

  // Step 2: Enforcement Rules
  const [enforcementRules, setEnforcementRules] = useState(
    gov.enforcementRules || []
  );
  const [rateLimitPerMinute, setRateLimitPerMinute] = useState(
    gov.rateLimitPerMinute || 60
  );

  // Step 2b: Pattern Template Selection
  const [availablePatterns, setAvailablePatterns] = useState([]);
  const [selectedPatternIds, setSelectedPatternIds] = useState(
    gov.selectedPatternIds || []
  );
  const [loadingPatterns, setLoadingPatterns] = useState(false);
  const [patternDomainFilter, setPatternDomainFilter] = useState('');

  // Step 3: Cross-Visibility
  const [unifiedView, setUnifiedView] = useState(null);
  const [loadingUnifiedView, setLoadingUnifiedView] = useState(false);

  // Step 3: Compliance Framework Assignment
  const [availableFrameworks, setAvailableFrameworks] = useState([]);
  const [existingBindings, setExistingBindings] = useState([]);
  const [selectedFrameworkIds, setSelectedFrameworkIds] = useState([]);
  const [loadingFrameworks, setLoadingFrameworks] = useState(false);
  const [bindingInProgress, setBindingInProgress] = useState(false);
  const [bindingLog, setBindingLog] = useState([]); // Progress log for binding orchestration
  const [frameworkSectorFilter, setFrameworkSectorFilter] = useState('');

  // Step 5: Activation — initialize from persisted state
  const [materializing, setMaterializing] = useState(false);
  const [materialized, setMaterialized] = useState(!!gov.materializedAt);
  const [activating, setActivating] = useState(false);
  const [activated, setActivated] = useState(!!gov.activatedAt);

  // Build the structured governance policy payload — preserves prior timestamps
  const buildPolicyPayload = useCallback((overrides = {}) => ({
    dataClassification,
    maxAutonomyLevel,
    dpgScanLevel,
    requiredApprovals: parseInt(requiredApprovals) || 0,
    rateLimitPerMinute: parseInt(rateLimitPerMinute) || 60,
    enforcementRules,
    selectedPatternIds, // Pattern template IDs from the library
    // Metadata
    configuredAt: new Date().toISOString(),
    configVersion: 2, // v2 = structured policy (vs v1 = flat 5-field form)
    // Lifecycle timestamps — persisted so wizard can detect state on re-entry
    materializedAt: gov.materializedAt || null,
    activatedAt: gov.activatedAt || null,
    ...overrides,
  }), [dataClassification, maxAutonomyLevel, dpgScanLevel, requiredApprovals, rateLimitPerMinute, enforcementRules, selectedPatternIds, gov.materializedAt, gov.activatedAt]);

  // Add a custom enforcement rule
  const addRule = () => {
    setEnforcementRules(prev => [...prev, {
      id: crypto.randomUUID(),
      name: '',
      ruleType: 'REGEX',
      severity: 'WARN',
      description: '',
      config: {},
    }]);
  };

  const updateRule = (id, field, value) => {
    setEnforcementRules(prev => prev.map(r =>
      r.id === id ? { ...r, [field]: value } : r
    ));
  };

  const removeRule = (id) => {
    setEnforcementRules(prev => prev.filter(r => r.id !== id));
  };

  // Save policies and materialize through bridge
  const handleSaveAndMaterialize = async () => {
    setError(null);
    setSaving(true);
    try {
      const materializedTimestamp = new Date().toISOString();
      const payload = buildPolicyPayload({ materializedAt: materializedTimestamp });

      if (policySkill) {
        // W1 (D-1) — persist the governance set onto the Governance Policy Skill,
        // then materialize from it (bridge stamps source_policy_skill_id).
        const saveRes = await api.attachSkillPolicy(policySkill.agentId, policySkill.id, payload);
        if (!saveRes.ok) throw new Error(saveRes.error || 'Failed to save policy skill');
        const skillRes = await api.gelMaterializeProxySkills(registration.id);
        if (!skillRes.ok) throw new Error(skillRes.error || 'Failed to materialize proxy skills');
        const matRes = await api.gelMaterializeGovernancePolicySkill(registration.id, policySkill.id);
        if (!matRes.ok) throw new Error(matRes.error || 'Failed to materialize governance policy skill');
      } else {
        // 1. Save governance policies to registration (with materializedAt timestamp)
        const saveRes = await api.gelAttachGovernancePolicies(registration.id, payload);
        if (!saveRes.ok) throw new Error(saveRes.error || 'Failed to save policies');

        // 2. Materialize proxy skills (if not already done)
        const skillRes = await api.gelMaterializeProxySkills(registration.id);
        if (!skillRes.ok) throw new Error(skillRes.error || 'Failed to materialize proxy skills');

        // 3. Materialize policy configurations through bridge
        const policyRes = await api.gelMaterializePolicies(registration.id, payload);
        if (!policyRes.ok) throw new Error(policyRes.error || 'Failed to materialize policy configurations');
      }

      setMaterialized(true);

      // 4. Validate readiness
      const validRes = await api.gelValidatePolicyReadiness(registration.id);
      if (validRes.ok) setValidationResult(validRes.data);

      // 5. Load unified enforcement view for cross-visibility
      await loadUnifiedView();

      // 6. Refresh framework bindings (may have been affected by materialization)
      await loadExistingBindings();

      setStep(3); // Move to review step
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  // Activate policies
  const handleActivate = async () => {
    setError(null);
    setActivating(true);
    try {
      const activateRes = await api.gelActivatePolicies(registration.id);
      if (!activateRes.ok) throw new Error(activateRes.error || 'Failed to activate policies');
      setActivated(true);

      // Persist activatedAt timestamp so wizard remembers on re-entry.
      const activatedTimestamp = new Date().toISOString();
      const persistPayload = buildPolicyPayload({ activatedAt: activatedTimestamp });
      if (policySkill) {
        await api.attachSkillPolicy(policySkill.agentId, policySkill.id, persistPayload);
      } else {
        await api.gelAttachGovernancePolicies(registration.id, persistPayload);
      }

      // Re-validate
      const validRes = await api.gelValidatePolicyReadiness(registration.id);
      if (validRes.ok) setValidationResult(validRes.data);

      // Notify parent
      if (onComplete) onComplete(persistPayload);
    } catch (e) {
      setError(e.message);
    } finally {
      setActivating(false);
    }
  };

  // ═══ Smart mount: detect current lifecycle state and resume at correct step ═══
  useEffect(() => {
    // Policy-skill-aware (nullable semantics preserved for the !gov guard).
    const gov = policySkill ? policySkill.policyConfig : registration?.governancePolicies;
    if (!gov || gov.configVersion < 2) return; // No structured policy yet — stay at step 0

    // Load validation result regardless
    api.gelValidatePolicyReadiness(registration.id).then(res => {
      if (res.ok) {
        setValidationResult(res.data);
        const validData = res.data;

        // Determine the correct step to resume at based on persisted state
        if (gov.activatedAt) {
          // Policies were previously activated — show completed activate step
          setMaterialized(true);
          setActivated(true);
          setStep(4); // Activate step — shows "Enforcement Active" banner
          loadUnifiedView();
        } else if (gov.materializedAt && validData.totalProxySkills > 0) {
          // Materialized but not activated — show review step
          setMaterialized(true);
          setStep(3); // Review & Validate step
          loadUnifiedView();
        } else if (gov.configVersion >= 2) {
          // Policy configured but not yet materialized — go to frameworks step
          setStep(2); // Compliance Frameworks (ready to materialize)
        }
      }
    });

    // Also load cross-visibility data if already materialized
    if (gov.materializedAt) {
      loadUnifiedView();
      loadExistingBindings();
    }
  }, [registration?.id]); // eslint-disable-line

  // Load pattern templates from the library
  useEffect(() => {
    setLoadingPatterns(true);
    api.getEnforcementPatterns(patternDomainFilter || null).then(res => {
      const data = res?.data ?? res;
      setAvailablePatterns(Array.isArray(data) ? data : []);
    }).catch(() => setAvailablePatterns([]))
      .finally(() => setLoadingPatterns(false));
  }, [patternDomainFilter]);

  // Load unified enforcement view for cross-visibility
  const loadUnifiedView = useCallback(async () => {
    if (!registration?.id) return;
    setLoadingUnifiedView(true);
    try {
      const res = await api.gelGetUnifiedEnforcementView(registration.id);
      const data = res?.data ?? res;
      if (data && !data.error) setUnifiedView(data);
    } catch (e) {
      console.warn('Unified view not available:', e.message);
    } finally {
      setLoadingUnifiedView(false);
    }
  }, [registration?.id]);

  // Load available regulatory frameworks
  useEffect(() => {
    setLoadingFrameworks(true);
    const params = frameworkSectorFilter ? { sector: frameworkSectorFilter } : {};
    api.ccListFrameworks(params).then(res => {
      const data = res?.data ?? res;
      setAvailableFrameworks(Array.isArray(data) ? data : []);
    }).catch(() => setAvailableFrameworks([]))
      .finally(() => setLoadingFrameworks(false));
  }, [frameworkSectorFilter]);

  // Load existing framework bindings for this agent
  const loadExistingBindings = useCallback(async () => {
    if (!registration?.agentId) return;
    try {
      const res = await api.ccGetAgentFrameworkBindings(registration.agentId);
      const data = res?.data ?? res;
      setExistingBindings(Array.isArray(data) ? data : []);
    } catch (e) {
      console.warn('Could not load framework bindings:', e.message);
    }
  }, [registration?.agentId]);

  useEffect(() => { loadExistingBindings(); }, [loadExistingBindings]);

  // Orchestrate framework binding: create binding → advance through state machine
  const handleBindFrameworks = async () => {
    if (selectedFrameworkIds.length === 0) return;
    setBindingInProgress(true);
    setBindingLog([]);
    const log = (msg) => setBindingLog(prev => [...prev, { time: new Date().toLocaleTimeString(), msg }]);

    try {
      for (const fwId of selectedFrameworkIds) {
        const fw = availableFrameworks.find(f => f.id === fwId);
        const fwName = fw?.name || fw?.code || fwId;
        log(`Creating binding for ${fwName}...`);

        // 1a. Create ComplianceConfig entry (so Policy Config Control Config tab stays in sync)
        try {
          const configParams = { frameworkId: fwId };
          if (registration.orgId) configParams.orgId = registration.orgId;
          if (registration.tenantId) configParams.tenantId = registration.tenantId;
          if (registration.agentId) configParams.agentId = registration.agentId;
          await api.ccAddFrameworkRequirement(configParams);
          log(`  ✓ ComplianceConfig created (Policy Config sync)`);
        } catch (e) {
          log(`  ⚠ ComplianceConfig sync skipped: ${e.message}`);
        }

        // 1b. Create FrameworkBinding (idempotent — returns existing if duplicate)
        const createRes = await api.ccCreateFrameworkBinding({
          frameworkId: fwId,
          agentId: registration.agentId,
          agentName: registration.externalAgentName,
          orgId: registration.orgId,
          tenantId: registration.tenantId,
        });
        const binding = createRes?.data ?? createRes;
        if (!binding?.id) { log(`  ⚠ Failed to create binding for ${fwName}`); continue; }
        log(`  ✓ FrameworkBinding created (${binding.id.substring(0, 8)}…) — status: ${binding.status}`);

        // 2. If already past PENDING, skip advancement
        if (binding.status !== 'PENDING') {
          log(`  → Already at ${binding.status}, skipping advancement`);
          continue;
        }

        // 3. Advance to SKILL_CREATED — proxy skills are already materialized by the bridge
        log(`  Advancing to SKILL_CREATED...`);
        try {
          await api.ccAdvanceFrameworkBinding(binding.id, {
            targetStatus: 'SKILL_CREATED',
            policySkillName: `GEL: ${registration.externalAgentName}`,
            skillAutoCreated: true,
          });
          log(`  ✓ SKILL_CREATED`);
        } catch (e) { log(`  ⚠ SKILL_CREATED failed: ${e.message}`); continue; }

        // 4. Advance to RULES_GENERATED — build control mappings from framework controls
        log(`  Advancing to RULES_GENERATED...`);
        try {
          const controlMappings = (fw?.controls || []).slice(0, 50).map(ctrl => ({
            controlId: ctrl.id || ctrl.controlId,
            controlCode: ctrl.code || ctrl.controlCode || ctrl.controlId,
            controlName: ctrl.name || ctrl.title || ctrl.controlCode,
            mappingSource: 'PATTERN_MATCH',
            severity: 'BLOCK',
          }));
          await api.ccAdvanceFrameworkBinding(binding.id, {
            targetStatus: 'RULES_GENERATED',
            controlMappings: controlMappings.length > 0 ? controlMappings : [{ controlCode: 'AUTO', controlName: 'Auto-mapped', mappingSource: 'PATTERN_MATCH', severity: 'BLOCK' }],
          });
          log(`  ✓ RULES_GENERATED (${controlMappings.length || 1} control mappings)`);
        } catch (e) { log(`  ⚠ RULES_GENERATED failed: ${e.message}`); continue; }

        // 5. Advance to TESTING
        log(`  Advancing to TESTING...`);
        try {
          await api.ccAdvanceFrameworkBinding(binding.id, { targetStatus: 'TESTING' });
          log(`  ✓ TESTING mode activated`);
        } catch (e) { log(`  ⚠ TESTING failed: ${e.message}`); continue; }

        log(`  ✓ ${fwName} bound successfully — ready for activation`);
      }

      log('Framework binding complete. Refresh bindings...');
      await loadExistingBindings();
      setSelectedFrameworkIds([]);
    } catch (e) {
      log(`Error: ${e.message}`);
    } finally {
      setBindingInProgress(false);
    }
  };

  // Activate a single binding (TESTING → ACTIVE)
  const handleActivateBinding = async (bindingId) => {
    try {
      await api.ccAdvanceFrameworkBinding(bindingId, { targetStatus: 'ACTIVE' });
      await loadExistingBindings();
    } catch (e) {
      setError(`Failed to activate binding: ${e.message}`);
    }
  };

  // Resume advancement for a PENDING or partially-advanced binding
  const handleResumeBinding = async (binding) => {
    setBindingInProgress(true);
    setBindingLog([]);
    const log = (msg) => setBindingLog(prev => [...prev, { time: new Date().toLocaleTimeString(), msg }]);
    const fwName = binding.frameworkName || binding.frameworkCode || 'Framework';

    try {
      log(`Resuming advancement for ${fwName} (current: ${binding.status})...`);

      // Advance through remaining states based on current status
      if (binding.status === 'PENDING') {
        log(`  Advancing to SKILL_CREATED...`);
        try {
          await api.ccAdvanceFrameworkBinding(binding.id, {
            targetStatus: 'SKILL_CREATED',
            policySkillName: `GEL: ${registration.externalAgentName}`,
            skillAutoCreated: true,
          });
          log(`  ✓ SKILL_CREATED`);
        } catch (e) { log(`  ⚠ SKILL_CREATED failed: ${e.message}`); setBindingInProgress(false); return; }
      }

      if (binding.status === 'PENDING' || binding.status === 'SKILL_CREATED') {
        log(`  Advancing to RULES_GENERATED...`);
        try {
          // Fetch framework controls for mapping
          let controlMappings = [{ controlCode: 'AUTO', controlName: 'Auto-mapped from GEL', mappingSource: 'PATTERN_MATCH', severity: 'BLOCK' }];
          try {
            const ctrlRes = await api.ccGetFrameworkControls?.(binding.frameworkId);
            const controls = ctrlRes?.ok ? (Array.isArray(ctrlRes.data) ? ctrlRes.data : ctrlRes.data?.controls || []) : [];
            if (controls.length > 0) {
              controlMappings = controls.slice(0, 50).map(ctrl => ({
                controlId: ctrl.id || ctrl.controlId,
                controlCode: ctrl.code || ctrl.controlCode || ctrl.controlId,
                controlName: ctrl.name || ctrl.title || ctrl.controlCode,
                mappingSource: 'PATTERN_MATCH',
                severity: 'BLOCK',
              }));
            }
          } catch (_) {}
          await api.ccAdvanceFrameworkBinding(binding.id, {
            targetStatus: 'RULES_GENERATED',
            controlMappings,
          });
          log(`  ✓ RULES_GENERATED (${controlMappings.length} control mappings)`);
        } catch (e) { log(`  ⚠ RULES_GENERATED failed: ${e.message}`); setBindingInProgress(false); return; }
      }

      if (['PENDING', 'SKILL_CREATED', 'RULES_GENERATED'].includes(binding.status)) {
        log(`  Advancing to TESTING...`);
        try {
          await api.ccAdvanceFrameworkBinding(binding.id, { targetStatus: 'TESTING' });
          log(`  ✓ TESTING mode activated`);
        } catch (e) { log(`  ⚠ TESTING failed: ${e.message}`); setBindingInProgress(false); return; }
      }

      log(`  ✓ ${fwName} advanced — ready for activation`);
      await loadExistingBindings();
    } catch (e) {
      log(`  Error: ${e.message}`);
    } finally {
      setBindingInProgress(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════════
  // Render
  // ═══════════════════════════════════════════════════════════════════

  return (
    <div style={{ maxWidth: 720 }}>
      {/* Step indicator */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20 }}>
        {STEPS.map((s, i) => {
          const Icon = s.icon;
          const isActive = i === step;
          const isPast = i < step;
          return (
            <div key={s.key} style={{
              flex: 1, padding: '8px 12px', borderRadius: 8, cursor: isPast ? 'pointer' : 'default',
              background: isActive ? BRAND.light : isPast ? ACCENT.emerald.bg : STONE[50],
              border: `1px solid ${isActive ? BRAND.primary : isPast ? ACCENT.emerald.border : STONE[200]}`,
              display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: isActive ? 600 : 400,
              color: isActive ? BRAND.primary : isPast ? ACCENT.emerald.text : STONE[500],
            }} onClick={() => isPast && setStep(i)}>
              {isPast ? <Check size={14} /> : <Icon size={14} />}
              {s.label}
            </div>
          );
        })}
      </div>

      {error && (
        <div style={{
          padding: '10px 14px', borderRadius: 8, marginBottom: 12,
          background: ACCENT.red.bg, border: `1px solid ${ACCENT.red.border}`,
          color: ACCENT.red.text, fontSize: 13, display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <AlertTriangle size={14} /> {error}
        </div>
      )}

      {/* ═══ Step 1: Classification & Autonomy ═══ */}
      {step === 0 && (
        <div>
          {/* Data Classification */}
          <div style={cardStyle}>
            <div style={{ fontWeight: 600, fontSize: 14, color: STONE[900], marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
              <Database size={15} /> Data Classification
            </div>
            <div style={{ fontSize: 12, color: STONE[500], marginBottom: 12 }}>
              What is the highest sensitivity level of data this external agent will process?
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {DATA_CLASSIFICATIONS.map(dc => (
                <div key={dc.value} onClick={() => setDataClassification(dc.value)} style={{
                  padding: '10px 14px', borderRadius: 8, cursor: 'pointer',
                  background: dataClassification === dc.value ? CLASSIFICATION_TONE[dc.value].bg : '#fff',
                  border: `2px solid ${dataClassification === dc.value ? CLASSIFICATION_TONE[dc.value].border : STONE[200]}`,
                }}>
                  <div style={{ fontWeight: 600, fontSize: 13, color: dataClassification === dc.value ? CLASSIFICATION_TONE[dc.value].text : STONE[700] }}>
                    {dc.label}
                  </div>
                  <div style={{ fontSize: 11, color: STONE[500], marginTop: 2 }}>{dc.desc}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Autonomy Level */}
          <div style={cardStyle}>
            <div style={{ fontWeight: 600, fontSize: 14, color: STONE[900], marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
              <Shield size={15} /> Maximum Autonomy Level
            </div>
            <div style={{ fontSize: 12, color: STONE[500], marginBottom: 12 }}>
              Caps the autonomy of all proxy skills created for this external agent.
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {AUTONOMY_CHOICES.map(al => {
                const Icon = AUTONOMY_ICON[al.value];
                return (
                  <div key={al.value} onClick={() => setMaxAutonomyLevel(al.value)} style={{
                    padding: '10px 14px', borderRadius: 8, cursor: 'pointer',
                    background: maxAutonomyLevel === al.value ? ACCENT.indigo.bg : '#fff',
                    border: `2px solid ${maxAutonomyLevel === al.value ? ACCENT.indigo.border : STONE[200]}`,
                    display: 'flex', alignItems: 'center', gap: 10,
                  }}>
                    <Icon size={16} color={maxAutonomyLevel === al.value ? ACCENT.indigo.text : STONE[400]} />
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 13, color: maxAutonomyLevel === al.value ? ACCENT.indigo.text : STONE[700] }}>
                        {al.label}
                      </div>
                      <div style={{ fontSize: 11, color: STONE[500] }}>{al.desc}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* DPG Scan Level */}
          <div style={cardStyle}>
            <div style={{ fontWeight: 600, fontSize: 14, color: STONE[900], marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
              <Lock size={15} /> Data Protection Gate (DPG) Scan Level
            </div>
            <select value={dpgScanLevel} onChange={e => setDpgScanLevel(e.target.value)} style={selectStyle}>
              {DPG_SCAN_LEVELS.map(d => (
                <option key={d.value} value={d.value}>{d.label} — {d.desc}</option>
              ))}
            </select>
          </div>

          {/* Required Approvals */}
          <div style={cardStyle}>
            <div style={{ fontWeight: 600, fontSize: 14, color: STONE[900], marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
              <Eye size={15} /> Required Approvals
            </div>
            <div style={{ fontSize: 12, color: STONE[500], marginBottom: 8 }}>
              Number of human approvals required before execution (0 = no approval gate).
            </div>
            <input type="number" min={0} max={5} value={requiredApprovals}
              onChange={e => setRequiredApprovals(e.target.value)} style={{ ...inputStyle, width: 80 }} />
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
            {onCancel && <button onClick={onCancel} style={btnSecondary}>Cancel</button>}
            <button onClick={() => setStep(1)} style={btnPrimary}>
              Next: Enforcement Rules <ArrowRight size={14} />
            </button>
          </div>
        </div>
      )}

      {/* ═══ Step 2: Enforcement Rules ═══ */}
      {step === 1 && (
        <div>
          {/* Rate Limit */}
          <div style={cardStyle}>
            <div style={{ fontWeight: 600, fontSize: 14, color: STONE[900], marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
              <Activity size={15} /> Rate Limiting
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 13, color: STONE[600] }}>Max</span>
              <input type="number" min={1} max={10000} value={rateLimitPerMinute}
                onChange={e => setRateLimitPerMinute(e.target.value)}
                style={{ ...inputStyle, width: 100 }} />
              <span style={{ fontSize: 13, color: STONE[600] }}>requests per minute</span>
            </div>
          </div>

          {/* Custom Rules */}
          <div style={cardStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div style={{ fontWeight: 600, fontSize: 14, color: STONE[900], display: 'flex', alignItems: 'center', gap: 6 }}>
                <Layers size={15} /> Custom Enforcement Rules
              </div>
              <button onClick={addRule} style={{
                ...btnSecondary, padding: '6px 12px', fontSize: 12,
              }}>
                <Plus size={12} /> Add Rule
              </button>
            </div>

            {enforcementRules.length === 0 && (
              <div style={{ padding: 16, textAlign: 'center', color: STONE[400], fontSize: 13 }}>
                No custom rules defined. Default enforcement (classification, autonomy, rate limit, DPG) will still apply.
              </div>
            )}

            {enforcementRules.map((rule, idx) => (
              <div key={rule.id} style={{
                padding: 12, borderRadius: 8, border: `1px solid ${STONE[200]}`,
                marginBottom: 8, background: STONE[50],
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: STONE[600] }}>Rule #{idx + 1}</span>
                  <button onClick={() => removeRule(rule.id)} style={{
                    background: 'none', border: 'none', cursor: 'pointer', color: ACCENT.red.text,
                  }}>
                    <Trash2 size={14} />
                  </button>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
                  <div>
                    <label style={{ fontSize: 11, color: STONE[500], display: 'block', marginBottom: 4 }}>Rule Name</label>
                    <input value={rule.name} onChange={e => updateRule(rule.id, 'name', e.target.value)}
                      placeholder="e.g., block_api_keys" style={inputStyle} />
                  </div>
                  <div>
                    <label style={{ fontSize: 11, color: STONE[500], display: 'block', marginBottom: 4 }}>Type</label>
                    <select value={rule.ruleType} onChange={e => updateRule(rule.id, 'ruleType', e.target.value)} style={selectStyle}>
                      {RULE_TYPES.map(rt => <option key={rt.value} value={rt.value}>{rt.label}</option>)}
                    </select>
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 8, marginBottom: 8 }}>
                  <div>
                    <label style={{ fontSize: 11, color: STONE[500], display: 'block', marginBottom: 4 }}>Description</label>
                    <input value={rule.description} onChange={e => updateRule(rule.id, 'description', e.target.value)}
                      placeholder="What does this rule enforce?" style={inputStyle} />
                  </div>
                  <div>
                    <label style={{ fontSize: 11, color: STONE[500], display: 'block', marginBottom: 4 }}>Severity</label>
                    <select value={rule.severity} onChange={e => updateRule(rule.id, 'severity', e.target.value)} style={selectStyle}>
                      {SEVERITIES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                </div>

                {/* Rule-type-specific config */}
                {rule.ruleType === 'REGEX' && (
                  <div>
                    <label style={{ fontSize: 11, color: STONE[500], display: 'block', marginBottom: 4 }}>Regex Pattern</label>
                    <input value={rule.config?.pattern || ''} placeholder="e.g., (sk-|api_key|secret)"
                      onChange={e => updateRule(rule.id, 'config', { ...rule.config, pattern: e.target.value })}
                      style={{ ...inputStyle, fontFamily: 'Consolas, monospace' }} />
                  </div>
                )}
                {rule.ruleType === 'RANGE' && (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                    <div>
                      <label style={{ fontSize: 11, color: STONE[500], display: 'block', marginBottom: 4 }}>Field</label>
                      <input value={rule.config?.field || ''} onChange={e => updateRule(rule.id, 'config', { ...rule.config, field: e.target.value })}
                        placeholder="e.g., tokenCount" style={inputStyle} />
                    </div>
                    <div>
                      <label style={{ fontSize: 11, color: STONE[500], display: 'block', marginBottom: 4 }}>Min</label>
                      <input type="number" value={rule.config?.min ?? ''} onChange={e => updateRule(rule.id, 'config', { ...rule.config, min: parseInt(e.target.value) || 0 })}
                        style={inputStyle} />
                    </div>
                    <div>
                      <label style={{ fontSize: 11, color: STONE[500], display: 'block', marginBottom: 4 }}>Max</label>
                      <input type="number" value={rule.config?.max ?? ''} onChange={e => updateRule(rule.id, 'config', { ...rule.config, max: parseInt(e.target.value) || 0 })}
                        style={inputStyle} />
                    </div>
                  </div>
                )}
                {rule.ruleType === 'ENUM_MATCH' && (
                  <div>
                    <label style={{ fontSize: 11, color: STONE[500], display: 'block', marginBottom: 4 }}>Allowed Values (comma-separated)</label>
                    <input value={(rule.config?.allowedValues || []).join(', ')}
                      onChange={e => updateRule(rule.id, 'config', { ...rule.config, allowedValues: e.target.value.split(',').map(v => v.trim()).filter(Boolean) })}
                      placeholder="e.g., text, code, analysis" style={inputStyle} />
                  </div>
                )}
                {rule.ruleType === 'RATE_LIMIT' && (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    <div>
                      <label style={{ fontSize: 11, color: STONE[500], display: 'block', marginBottom: 4 }}>Max Requests</label>
                      {/* Canonical key the engine reads (PolicyRuleEngine.evaluateRateLimit). */}
                      <input type="number" min="1" value={rule.config?.maxRequests ?? 10}
                        onChange={e => updateRule(rule.id, 'config', { ...rule.config, maxRequests: parseInt(e.target.value) || 1 })}
                        style={inputStyle} />
                    </div>
                    <div>
                      <label style={{ fontSize: 11, color: STONE[500], display: 'block', marginBottom: 4 }}>Window (minutes)</label>
                      <input type="number" min="1" value={rule.config?.windowMinutes ?? 60}
                        onChange={e => updateRule(rule.id, 'config', { ...rule.config, windowMinutes: parseInt(e.target.value) || 1 })}
                        style={inputStyle} />
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Framework Pattern Templates */}
          <div style={cardStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div style={{ fontWeight: 600, fontSize: 14, color: STONE[900], display: 'flex', alignItems: 'center', gap: 6 }}>
                <Shield size={15} color={ACCENT.indigo.text} /> Framework Pattern Templates
              </div>
              <select value={patternDomainFilter} onChange={e => setPatternDomainFilter(e.target.value)} style={{ ...selectStyle, width: 180 }}>
                <option value="">All Domains</option>
                <option value="SECURITY">Security</option>
                <option value="ACCESS_CONTROL">Access Control</option>
                <option value="DATA_CLASSIFICATION">Data Classification</option>
                <option value="CHANGE_MANAGEMENT">Change Management</option>
                <option value="OPERATIONAL">Operational</option>
                <option value="COMPLIANCE">Compliance</option>
              </select>
            </div>
            <div style={{ fontSize: 12, color: STONE[500], marginBottom: 10 }}>
              Select industry-standard enforcement patterns to apply to this external agent. These are the same patterns used by Policy Config for native agents.
            </div>

            {selectedPatternIds.length > 0 && (
              <div style={{
                padding: '6px 10px', borderRadius: 6, marginBottom: 10,
                background: ACCENT.indigo.bg, border: `1px solid ${ACCENT.indigo.border}`,
                fontSize: 12, fontWeight: 600, color: ACCENT.indigo.text,
              }}>
                {selectedPatternIds.length} pattern(s) selected
              </div>
            )}

            {loadingPatterns ? (
              <div style={{ padding: 16, textAlign: 'center', color: STONE[400], fontSize: 13 }}>
                Loading patterns...
              </div>
            ) : availablePatterns.length === 0 ? (
              <div style={{ padding: 16, textAlign: 'center', color: STONE[400], fontSize: 13 }}>
                No patterns available. Patterns are managed in Policy Config &gt; Pattern Library.
              </div>
            ) : (
              <div style={{ maxHeight: 260, overflowY: 'auto' }}>
                {availablePatterns.map(p => {
                  const isSelected = selectedPatternIds.includes(p.id);
                  return (
                    <div key={p.id} onClick={() => {
                      setSelectedPatternIds(prev =>
                        isSelected ? prev.filter(id => id !== p.id) : [...prev, p.id]
                      );
                    }} style={{
                      display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px',
                      borderRadius: 6, marginBottom: 4, cursor: 'pointer',
                      background: isSelected ? ACCENT.indigo.bg : '#fff',
                      border: `1px solid ${isSelected ? ACCENT.indigo.border : STONE[200]}`,
                    }}>
                      <div style={{
                        width: 18, height: 18, borderRadius: 4, flexShrink: 0,
                        border: `2px solid ${isSelected ? ACCENT.indigo.text : STONE[300]}`,
                        background: isSelected ? ACCENT.indigo.text : '#fff',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        {isSelected && <Check size={12} color="#fff" />}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: STONE[800] }}>{p.patternName}</div>
                        <div style={{ fontSize: 11, color: STONE[500], display: 'flex', gap: 6, marginTop: 2 }}>
                          <span style={{
                            fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 3,
                            background: ACCENT.sky.bg, color: ACCENT.sky.text, border: `1px solid ${ACCENT.sky.border}`,
                          }}>{p.domain}</span>
                          <span>{p.mapsToRuleType}</span>
                        </div>
                      </div>
                      {!p.tenantId && (
                        <span style={{ fontSize: 9, color: STONE[400], fontWeight: 600, textTransform: 'uppercase' }}>
                          Platform
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 16 }}>
            <button onClick={() => setStep(0)} style={btnSecondary}>
              <ArrowLeft size={14} /> Back
            </button>
            <button onClick={() => setStep(2)} style={btnPrimary}>
              Next: Compliance Frameworks <ArrowRight size={14} />
            </button>
          </div>
        </div>
      )}

      {/* ═══ Step 3: Compliance Frameworks ═══ */}
      {step === 2 && (
        <div>
          {/* Existing Bindings */}
          <div style={cardStyle}>
            <div style={{ fontWeight: 600, fontSize: 14, color: STONE[900], marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
              <BookOpen size={15} /> Current Framework Bindings
            </div>
            <div style={{ fontSize: 12, color: STONE[500], marginBottom: 12 }}>
              Regulatory frameworks already bound to this external agent. These are the same frameworks used in Compliance Center for native agents.
            </div>

            {existingBindings.length === 0 ? (
              <div style={{ padding: 16, textAlign: 'center', color: STONE[400], fontSize: 13, background: STONE[50], borderRadius: 8 }}>
                No frameworks bound yet. Select frameworks below to assign compliance controls.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {existingBindings.map(b => {
                  const statusColors = BINDING_STATUS_COLORS[b.status] || BINDING_STATUS_COLORS.PENDING;
                  const coverage = b.coverageMetrics?.combinedCoveragePercent ?? b.coverageMetrics?.coveragePercent;
                  return (
                    <div key={b.id} style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '10px 14px', borderRadius: 8,
                      background: '#fff', border: `1px solid ${statusColors.border}`,
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1 }}>
                        <ShieldCheck size={16} color={statusColors.text} />
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 600, color: STONE[800] }}>
                            {b.frameworkName || b.frameworkCode || 'Framework'}
                          </div>
                          <div style={{ fontSize: 11, color: STONE[500], display: 'flex', gap: 8, marginTop: 2, flexWrap: 'wrap' }}>
                            {b.frameworkCode && <span>{b.frameworkCode}</span>}
                            {coverage != null && (
                              <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                                <BarChart3 size={10} /> {Math.round(coverage)}% coverage
                              </span>
                            )}
                            {b.coverageMetrics?.totalControls > 0 && (
                              <span>{b.coverageMetrics.totalControls} controls</span>
                            )}
                            {b.coverageMetrics?.automatableControls > 0 && (
                              <span style={{ color: ACCENT.emerald.text }}>
                                {b.coverageMetrics.automatableControls} automatable
                              </span>
                            )}
                            {b.coverageMetrics?.manualOnlyControls > 0 && (
                              <span style={{ color: ACCENT.amber.text }}>
                                {b.coverageMetrics.manualOnlyControls} manual
                              </span>
                            )}
                            {b.coverageMetrics?.gapControls?.length > 0 && (
                              <span style={{ color: ACCENT.red.text }}>
                                {b.coverageMetrics.gapControls.length} gaps
                              </span>
                            )}
                            {b.controlMappings?.length > 0 && !b.coverageMetrics?.totalControls && (
                              <span>{b.controlMappings.length} control mappings</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{
                          fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 4,
                          background: statusColors.bg, color: statusColors.text,
                          border: `1px solid ${statusColors.border}`,
                        }}>{b.status}</span>
                        {['PENDING', 'SKILL_CREATED', 'RULES_GENERATED'].includes(b.status) && (
                          <button onClick={() => handleResumeBinding(b)} disabled={bindingInProgress} style={{
                            ...btnPrimary, padding: '4px 10px', fontSize: 11,
                            background: ACCENT.indigo.text, opacity: bindingInProgress ? 0.6 : 1,
                          }}>
                            <RefreshCw size={10} /> Advance
                          </button>
                        )}
                        {b.status === 'TESTING' && (
                          <button onClick={() => handleActivateBinding(b.id)} style={{
                            ...btnPrimary, padding: '4px 10px', fontSize: 11,
                          }}>
                            <Zap size={10} /> Activate
                          </button>
                        )}
                        {['TESTING', 'ACTIVE'].includes(b.status) && (
                          <button onClick={() => window.open(`${window.location.origin}/policy-config?framework=${b.frameworkId || b.frameworkKey}`, '_blank')} style={{
                            ...btnSecondary, padding: '4px 10px', fontSize: 11,
                          }}>
                            <Settings size={10} /> Controls
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Control Management Guide */}
          {existingBindings.some(b => ['TESTING', 'ACTIVE'].includes(b.status)) && (
            <div style={{
              padding: '12px 16px', borderRadius: 8, marginBottom: 12,
              background: ACCENT.sky.bg, border: `1px solid ${ACCENT.sky.border}`,
              fontSize: 12, color: ACCENT.sky.text, lineHeight: 1.5,
            }}>
              <div style={{ fontWeight: 600, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                <BookOpen size={13} /> Control-Level Management
              </div>
              <div style={{ color: STONE[600] }}>
                This wizard auto-maps automatable controls to enforcement rules for quick-start protection.
                For <strong>granular control selection</strong>, <strong>automatable vs manual classification</strong>,
                and <strong>manual attestation workflows</strong>, use{' '}
                <a href="/policy-config" style={{ color: ACCENT.indigo.text, fontWeight: 600 }}>
                  Policy Config → Framework Coverage
                </a>{' '}
                — it provides per-control drill-down, gap analysis, and attestation task creation for controls
                that cannot be automated.
              </div>
            </div>
          )}

          {/* Available Frameworks to Bind */}
          <div style={cardStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div style={{ fontWeight: 600, fontSize: 14, color: STONE[900], display: 'flex', alignItems: 'center', gap: 6 }}>
                <Plus size={15} /> Assign New Frameworks
              </div>
              <select value={frameworkSectorFilter} onChange={e => setFrameworkSectorFilter(e.target.value)} style={{ ...selectStyle, width: 180 }}>
                <option value="">All Sectors</option>
                <option value="GENERAL">General</option>
                <option value="HEALTHCARE">Healthcare</option>
                <option value="FINANCE">Finance</option>
                <option value="GOVERNMENT">Government</option>
                <option value="TECHNOLOGY">Technology</option>
              </select>
            </div>

            {selectedFrameworkIds.length > 0 && (
              <div style={{
                padding: '6px 10px', borderRadius: 6, marginBottom: 10,
                background: ACCENT.indigo.bg, border: `1px solid ${ACCENT.indigo.border}`,
                fontSize: 12, fontWeight: 600, color: ACCENT.indigo.text,
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              }}>
                <span>{selectedFrameworkIds.length} framework(s) selected for binding</span>
                <button onClick={handleBindFrameworks} disabled={bindingInProgress} style={{
                  ...btnPrimary, padding: '4px 12px', fontSize: 11,
                  opacity: bindingInProgress ? 0.6 : 1,
                }}>
                  {bindingInProgress ? <><RefreshCw size={10} /> Binding...</> : <><Link2 size={10} /> Bind Selected</>}
                </button>
              </div>
            )}

            {loadingFrameworks ? (
              <div style={{ padding: 16, textAlign: 'center', color: STONE[400], fontSize: 13 }}>
                Loading frameworks...
              </div>
            ) : availableFrameworks.length === 0 ? (
              <div style={{ padding: 16, textAlign: 'center', color: STONE[400], fontSize: 13 }}>
                No frameworks available. Frameworks are managed in Compliance Center.
              </div>
            ) : (
              <div style={{ maxHeight: 280, overflowY: 'auto' }}>
                {availableFrameworks
                  .filter(fw => !existingBindings.some(b => b.frameworkId === fw.id && b.status !== 'ARCHIVED' && b.status !== 'FAILED'))
                  .map(fw => {
                    const isSelected = selectedFrameworkIds.includes(fw.id);
                    return (
                      <div key={fw.id} onClick={() => {
                        setSelectedFrameworkIds(prev =>
                          isSelected ? prev.filter(id => id !== fw.id) : [...prev, fw.id]
                        );
                      }} style={{
                        display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px',
                        borderRadius: 6, marginBottom: 4, cursor: 'pointer',
                        background: isSelected ? ACCENT.indigo.bg : '#fff',
                        border: `1px solid ${isSelected ? ACCENT.indigo.border : STONE[200]}`,
                      }}>
                        <div style={{
                          width: 18, height: 18, borderRadius: 4, flexShrink: 0,
                          border: `2px solid ${isSelected ? ACCENT.indigo.text : STONE[300]}`,
                          background: isSelected ? ACCENT.indigo.text : '#fff',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                          {isSelected && <Check size={12} color="#fff" />}
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: STONE[800] }}>
                            {fw.name || fw.code}
                          </div>
                          <div style={{ fontSize: 11, color: STONE[500], display: 'flex', gap: 8, marginTop: 2 }}>
                            {fw.code && (
                              <span style={{
                                fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 3,
                                background: ACCENT.sky.bg, color: ACCENT.sky.text, border: `1px solid ${ACCENT.sky.border}`,
                              }}>{fw.code}</span>
                            )}
                            {fw.sector && <span>{fw.sector}</span>}
                            {fw.controls?.length > 0 && <span>{fw.controls.length} controls</span>}
                          </div>
                          {fw.description && (
                            <div style={{ fontSize: 11, color: STONE[400], marginTop: 3, lineHeight: 1.4 }}>
                              {fw.description.length > 120 ? fw.description.substring(0, 120) + '…' : fw.description}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
              </div>
            )}
          </div>

          {/* Binding Progress Log */}
          {bindingLog.length > 0 && (
            <div style={{
              ...cardStyle, background: STONE[900], border: `1px solid ${STONE[700]}`,
              maxHeight: 200, overflowY: 'auto',
            }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: STONE[400], marginBottom: 8 }}>
                Binding Orchestration Log
              </div>
              {bindingLog.map((entry, i) => (
                <div key={i} style={{ fontSize: 11, fontFamily: 'Consolas, monospace', color: STONE[300], lineHeight: 1.6 }}>
                  <span style={{ color: STONE[500] }}>{entry.time}</span>{' '}
                  {entry.msg}
                </div>
              ))}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 16 }}>
            <button onClick={() => setStep(1)} style={btnSecondary}>
              <ArrowLeft size={14} /> Back
            </button>
            <button onClick={handleSaveAndMaterialize} disabled={saving} style={{
              ...btnPrimary, opacity: saving ? 0.6 : 1,
            }}>
              {saving ? <><RefreshCw size={14} className="spin" /> Materializing...</> : <>Save & Materialize <ArrowRight size={14} /></>}
            </button>
          </div>
        </div>
      )}

      {/* ═══ Step 4: Review & Validate ═══ */}
      {step === 3 && (
        <div>
          {/* Policy Summary */}
          <div style={cardStyle}>
            <div style={{ fontWeight: 600, fontSize: 14, color: STONE[900], marginBottom: 12 }}>
              Protection Policy Summary
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
              <SummaryItem label="Data Classification" value={dataClassification} />
              <SummaryItem label="Max Autonomy" value={maxAutonomyLevel.replace('_', ' ')} />
              <SummaryItem label="DPG Scan Level" value={dpgScanLevel} />
              <SummaryItem label="Required Approvals" value={requiredApprovals} />
              <SummaryItem label="Rate Limit" value={`${rateLimitPerMinute} req/min`} />
              <SummaryItem label="Custom Rules" value={`${enforcementRules.length} defined`} />
              <SummaryItem label="Pattern Templates" value={`${selectedPatternIds.length} selected`} />
              <SummaryItem label="Framework Bindings" value={`${existingBindings.filter(b => b.status !== 'ARCHIVED' && b.status !== 'FAILED').length} bound`} />
            </div>
          </div>

          {/* Validation Result */}
          {validationResult && (
            <div style={{
              ...cardStyle,
              borderColor: validationResult.passed ? ACCENT.emerald.border : ACCENT.red.border,
              background: validationResult.passed ? ACCENT.emerald.bg : ACCENT.red.bg,
            }}>
              <div style={{
                fontWeight: 600, fontSize: 14, marginBottom: 8,
                color: validationResult.passed ? ACCENT.emerald.text : ACCENT.red.text,
                display: 'flex', alignItems: 'center', gap: 6,
              }}>
                {validationResult.passed ? <CheckCircle2 size={16} /> : <XCircle size={16} />}
                {validationResult.passed ? 'Policy Readiness: PASSED' : 'Policy Readiness: FAILED'}
              </div>

              <div style={{ fontSize: 12, color: STONE[600], marginBottom: 8 }}>
                {validationResult.totalProxySkills} proxy skill(s), {validationResult.activeConfigurations} active configuration(s)
              </div>

              {validationResult.failures?.length > 0 && (
                <div style={{ marginBottom: 8 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: ACCENT.red.text, marginBottom: 4 }}>Failures:</div>
                  {validationResult.failures.map((f, i) => (
                    <div key={i} style={{ fontSize: 12, color: ACCENT.red.text, paddingLeft: 12 }}>- {f}</div>
                  ))}
                </div>
              )}

              {validationResult.warnings?.length > 0 && (
                <div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: ACCENT.amber.text, marginBottom: 4 }}>Warnings:</div>
                  {validationResult.warnings.map((w, i) => (
                    <div key={i} style={{ fontSize: 12, color: ACCENT.amber.text, paddingLeft: 12 }}>- {w}</div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Enforcement rule list — GEL Bridge Rules */}
          <div style={cardStyle}>
            <div style={{ fontWeight: 600, fontSize: 14, color: STONE[900], marginBottom: 4 }}>
              GEL Bridge Enforcement Rules
            </div>
            <div style={{ fontSize: 12, color: STONE[500], marginBottom: 12 }}>
              Rules materialized from this wizard — standard PolicyRule entities evaluated at every execution.
            </div>

            {[
              { name: 'Rate Limit', desc: `${rateLimitPerMinute} req/min`, severity: 'BLOCK', source: 'bridge' },
              { name: 'Data Classification', desc: dataClassification, severity: dataClassification === 'RESTRICTED' || dataClassification === 'CONFIDENTIAL' ? 'BLOCK' : 'WARN', source: 'bridge' },
              { name: 'Autonomy Cap', desc: maxAutonomyLevel.replace('_', ' '), severity: 'BLOCK', source: 'bridge' },
              { name: 'DPG Scan Level', desc: dpgScanLevel, severity: 'AUDIT', source: 'bridge' },
              ...(requiredApprovals > 0 ? [{ name: 'Approval Gate', desc: `${requiredApprovals} approval(s)`, severity: 'BLOCK', source: 'bridge' }] : []),
              ...enforcementRules.map(r => ({ name: r.name || 'Custom', desc: r.description || r.ruleType, severity: r.severity, source: 'custom' })),
              ...selectedPatternIds.map(pid => {
                const p = availablePatterns.find(ap => ap.id === pid);
                return p ? { name: p.patternName, desc: p.domain + ' — ' + p.mapsToRuleType, severity: 'BLOCK', source: 'pattern' } : null;
              }).filter(Boolean),
            ].map((r, i) => (
              <div key={i} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '8px 12px', borderRadius: 6, marginBottom: 4,
                background: r.source === 'pattern' ? ACCENT.indigo.bg : STONE[50],
                border: `1px solid ${r.source === 'pattern' ? ACCENT.indigo.border : STONE[200]}`,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Shield size={12} color={r.source === 'pattern' ? ACCENT.indigo.text : STONE[400]} />
                  <span style={{ fontSize: 13, fontWeight: 500, color: STONE[700] }}>{r.name}</span>
                  <span style={{ fontSize: 12, color: STONE[500] }}>{r.desc}</span>
                  {r.source === 'pattern' && (
                    <span style={{
                      fontSize: 9, fontWeight: 600, padding: '1px 5px', borderRadius: 3,
                      background: ACCENT.indigo.text, color: '#fff',
                    }}>PATTERN</span>
                  )}
                </div>
                <span style={{
                  fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 4,
                  background: r.severity === 'BLOCK' ? ACCENT.red.bg : r.severity === 'WARN' ? ACCENT.amber.bg : ACCENT.sky.bg,
                  color: r.severity === 'BLOCK' ? ACCENT.red.text : r.severity === 'WARN' ? ACCENT.amber.text : ACCENT.sky.text,
                  border: `1px solid ${r.severity === 'BLOCK' ? ACCENT.red.border : r.severity === 'WARN' ? ACCENT.amber.border : ACCENT.sky.border}`,
                }}>{r.severity}</span>
              </div>
            ))}
          </div>

          {/* Cross-Visibility: Framework Bindings from Policy Config / Compliance Center */}
          {unifiedView && (unifiedView.frameworkBindings?.length > 0 || unifiedView.bridgeRules?.length > 0) && (
            <div style={{
              ...cardStyle,
              background: '#f8f7ff',
              borderColor: ACCENT.indigo.border,
            }}>
              <div style={{ fontWeight: 600, fontSize: 14, color: ACCENT.indigo.text, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                <Layers size={15} /> Cross-System Enforcement (Policy Config / Compliance Center)
              </div>
              <div style={{ fontSize: 12, color: STONE[500], marginBottom: 12 }}>
                Additional enforcement rules bound to this agent via Policy Config or Compliance Center framework assignments. These are evaluated alongside GEL bridge rules at runtime.
              </div>

              {/* Framework Bindings */}
              {unifiedView.frameworkBindings?.length > 0 && (
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: ACCENT.indigo.text, marginBottom: 6 }}>
                    Framework Bindings ({unifiedView.frameworkBindings.length})
                  </div>
                  {unifiedView.frameworkBindings.map((fb, i) => (
                    <div key={i} style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '8px 12px', borderRadius: 6, marginBottom: 4,
                      background: '#fff', border: `1px solid ${ACCENT.indigo.border}`,
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <ShieldCheck size={12} color={ACCENT.indigo.text} />
                        <span style={{ fontSize: 13, fontWeight: 600, color: STONE[800] }}>
                          {fb.frameworkName || fb.frameworkCode || 'Framework'}
                        </span>
                        {fb.coverageMetrics?.coveragePercent != null && (
                          <span style={{ fontSize: 11, color: STONE[500] }}>
                            {Math.round(fb.coverageMetrics.coveragePercent)}% coverage
                          </span>
                        )}
                      </div>
                      <span style={{
                        fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 4,
                        background: fb.status === 'ACTIVE' ? ACCENT.emerald.bg : ACCENT.amber.bg,
                        color: fb.status === 'ACTIVE' ? ACCENT.emerald.text : ACCENT.amber.text,
                        border: `1px solid ${fb.status === 'ACTIVE' ? ACCENT.emerald.border : ACCENT.amber.border}`,
                      }}>{fb.status}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Summary */}
              <div style={{
                padding: '8px 12px', borderRadius: 6, background: '#fff',
                border: `1px solid ${ACCENT.indigo.border}`, fontSize: 12, color: STONE[600],
              }}>
                Total enforcement: <strong>{(unifiedView.bridgeRuleCount || 0)}</strong> bridge rules +{' '}
                <strong>{(unifiedView.frameworkBindingCount || 0)}</strong> framework bindings — all evaluated by the same PolicyEnforcementGate pipeline.
              </div>
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 16 }}>
            <button onClick={() => setStep(2)} style={btnSecondary}>
              <ArrowLeft size={14} /> Edit Frameworks
            </button>
            <button onClick={() => setStep(4)} style={{
              ...btnPrimary,
              opacity: validationResult && !validationResult.passed ? 0.5 : 1,
            }} disabled={validationResult && !validationResult.passed}>
              Next: Activate <ArrowRight size={14} />
            </button>
          </div>
        </div>
      )}

      {/* ═══ Step 5: Activate ═══ */}
      {step === 4 && (
        <div>
          <div style={cardStyle}>
            <div style={{ fontWeight: 600, fontSize: 14, color: STONE[900], marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
              <Zap size={15} /> Activate Enforcement
            </div>
            <div style={{ fontSize: 13, color: STONE[600], marginBottom: 16, lineHeight: 1.5 }}>
              Activating will transition all proxy skill policy configurations from <strong>draft</strong> to <strong>active</strong>.
              Once active, PolicyEnforcementGate will evaluate these rules at every execution of this external agent —
              the same 6-stage pipeline used for native agents (deterministic rules, autonomy validation,
              system protection, RBAC, guardrails, policy RAG).
            </div>

            {activated ? (
              <div style={{
                padding: '16px 20px', borderRadius: 8,
                background: ACCENT.emerald.bg, border: `1px solid ${ACCENT.emerald.border}`,
                display: 'flex', alignItems: 'center', gap: 10,
              }}>
                <CheckCircle2 size={20} color={ACCENT.emerald.text} />
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14, color: ACCENT.emerald.text }}>
                    Enforcement Active
                  </div>
                  <div style={{ fontSize: 12, color: STONE[600] }}>
                    All policy configurations are now live. The external agent is protected by the same
                    enforcement pipeline as native agents. Compliance Center will include this agent
                    in compliance calculations.
                  </div>
                </div>
              </div>
            ) : (
              <button onClick={handleActivate} disabled={activating} style={{
                ...btnPrimary, width: '100%', justifyContent: 'center',
                opacity: activating ? 0.6 : 1, padding: '12px 20px',
              }}>
                {activating
                  ? <><RefreshCw size={14} /> Activating...</>
                  : <><Zap size={14} /> Activate Policy Enforcement</>
                }
              </button>
            )}
          </div>

          {activated && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
              <button onClick={() => onComplete && onComplete(buildPolicyPayload())} style={btnPrimary}>
                Done <Check size={14} />
              </button>
            </div>
          )}

          {!activated && (
            <div style={{ display: 'flex', justifyContent: 'flex-start', marginTop: 16 }}>
              <button onClick={() => setStep(3)} style={btnSecondary}>
                <ArrowLeft size={14} /> Back to Review
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SummaryItem({ label, value }) {
  return (
    <div style={{ padding: '8px 12px', borderRadius: 6, background: STONE[50], border: `1px solid ${STONE[200]}` }}>
      <div style={{ fontSize: 11, color: STONE[500] }}>{label}</div>
      <div style={{ fontSize: 13, fontWeight: 600, color: STONE[700] }}>{String(value)}</div>
    </div>
  );
}
